import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import Button from "../../../components/ui/Button";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getAllProductsService } from "redux/services/general_service";

const SolutionSection = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [activeProduct, setActiveProduct] = useState(0);
  const [hoveredSolution, setHoveredSolution] = useState(null);
  const [products, setProducts] = useState([]); // State for products

  const solutions = [
    {
      id: 1,
      icon: "Clock",
      title: "متاح 24/7",
      description: "تسوق في أي وقت، طلبك محفوظ",
      color: "text-primary",
    },
    {
      id: 2,
      icon: "Shield",
      title: "ضمان الأصالة",
      description: "منتجات أصلية 100% أو استرداد المال",
      color: "text-success",
    },
    {
      id: 3,
      icon: "Truck",
      title: "توصيل سريع",
      description: "خلال ساعتين في نفس اليوم",
      color: "text-accent",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveProduct((prev) => (prev + 1) % products?.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [products]); // Dependency on products

  useEffect(() => {
    dispatch(getAllProductsService({ doctor_favorite: 1 })).then((response) => {
      if (response?.payload?.status === 200) {
        const fetchedProducts = response?.payload?.data?.data;
        setProducts(fetchedProducts);
      }
    });
  }, [dispatch]);

  return (
    <section className="py-20 bg-gradient-to-b from-muted to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16 mt-16">
          <h2 className="text-3xl md:text-4xl font-noto-arabic font-bold text-text-primary mb-6">
            روما: صيدليتك الذكية المتكاملة
          </h2>

          <p className="text-lg text-text-secondary font-cairo max-w-3xl mx-auto leading-relaxed">
            نحن لا نبيع المنتجات فقط، بل نقدم تجربة صحية متكاملة تجمع بين الثقة
            والجودة والراحة. اكتشف كيف نحول التسوق الصحي إلى متعة حقيقية.
          </p>
        </div>

        {/* Main Solution Grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          {/* Interactive Product Showcase */}
          <div className="space-y-6">
            <h3 className="text-2xl font-noto-arabic font-bold text-text-primary text-right mb-8">
              منتجات موصى بها من قبل الطبيب
            </h3>

            {/* Product Tabs */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {products?.map((product, index) => (
                <button
                  key={product?.id}
                  onClick={() => setActiveProduct(index)}
                  className={`p-4 rounded-xl border-2 transition-all duration-300 text-right ${
                    activeProduct === index
                      ? "border-primary bg-primary/5 shadow-md"
                      : "border-border hover:border-primary/50 hover:bg-muted"
                  }`}
                >
                  <div className="flex items-center justify-start gap-3">
                    <div>
                      <h4
                        className={`font-cairo font-semibold ${
                          activeProduct === index
                            ? "text-primary"
                            : "text-text-primary"
                        }`}
                      >
                        {product?.name}
                      </h4>
                      <p className="text-sm text-text-secondary">
                        {product?.price} دينار عراقي
                      </p>
                    </div>
                    <Icon
                      name="Star"
                      size={24}
                      className={
                        activeProduct === index
                          ? "text-primary"
                          : "text-text-secondary"
                      }
                    />
                  </div>
                </button>
              ))}
            </div>

            {/* Active Product Details */}
            <div className="bg-white rounded-2xl p-6 shadow-card border border-border">
              <div className="text-right space-y-4">
                <div className="flex items-center justify-start gap-3">
                  <h4 className="text-xl font-noto-arabic font-bold text-text-primary">
                    {products?.[activeProduct]?.name}
                  </h4>
                  <Icon name="Star" size={28} className="text-primary" />
                </div>

                <p className="text-text-secondary font-cairo">
                  {products?.[activeProduct]?.desc || "لا يوجد وصف"}
                </p>

                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-primary">
                    {products?.[activeProduct]?.price_after_discount ||
                      products?.[activeProduct]?.price}{" "}
                    ريال
                  </span>
                  {products?.[activeProduct]?.has_discount && (
                    <span className="text-sm text-text-secondary line-through">
                      {products?.[activeProduct]?.price} ريال
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Icon name="Star" size={16} className="text-accent" />
                  <span className="text-sm font-cairo text-text-secondary">
                    {products?.[activeProduct]?.rate} (
                    {products?.[activeProduct]?.reviews} تقييم)
                  </span>
                </div>

           
              </div>
            </div>
          </div>

          {/* Product Image */}
          <div className="relative">
            <div className="relative w-full h-96 rounded-2xl overflow-hidden shadow-2xl">
              <Image
                src={
                  products?.[activeProduct]?.images?.[0]?.file_path ||
                  "https://via.placeholder.com/400"
                }
                alt={products?.[activeProduct]?.name}
                className="w-full h-full object-cover transition-all duration-700 ease-in-out"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

              {/* Floating Badge */}
              <div className="absolute top-6 right-6 bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg">
                <div className="flex items-center gap-2">
                  <Icon
                    name="Heart"
                    size={16}
                    className={
                      products?.[activeProduct]?.in_favorite
                        ? "text-red-500 fill-current"
                        : "text-gray-500"
                    }
                  />
                  <span className="font-bold text-text-primary">موصى به</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Solution Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {solutions?.map((solution) => (
            <div
              key={solution?.id}
              onMouseEnter={() => setHoveredSolution(solution?.id)}
              onMouseLeave={() => setHoveredSolution(null)}
              className={`bg-white rounded-xl p-6 shadow-card border border-border hover:shadow-lg transition-all duration-300 text-right ${
                hoveredSolution === solution?.id
                  ? "transform -translate-y-2"
                  : ""
              }`}
            >
              <div className="flex items-center justify-start gap-2 mb-4">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    hoveredSolution === solution?.id
                      ? "bg-primary/10"
                      : "bg-muted"
                  }`}
                >
                  <Icon
                    name={solution?.icon}
                    size={24}
                    className={
                      hoveredSolution === solution?.id
                        ? "text-primary"
                        : solution?.color
                    }
                  />
                </div>
                <h3 className="font-cairo font-bold text-text-primary">
                  {solution?.title}
                </h3>
              </div>

              <p className="text-text-secondary font-cairo text-sm leading-relaxed">
                {solution?.description}
              </p>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 rounded-2xl p-8 border border-primary/20">
          <h3 className="text-2xl font-noto-arabic font-bold text-text-primary mb-4">
            جرب الفرق بنفسك
          </h3>
          <p className="text-text-secondary font-cairo mb-6 max-w-2xl mx-auto">
            انضم إلى أكثر من 50,000 عميل راضٍ واكتشف لماذا روما هي الخيار الأول
            للصحة والجمال
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate("/product-listing")}
              variant="default"
              size="lg"
              className="font-tajawal font-bold shadow-cta"
            >
              <Icon name="ShoppingBag" size={20} className="ml-2" />
              ابدأ التسوق الآن
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;
